import React, { useContext, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthContext, AuthProvider } from './context/AuthContext';
import { ThemeProvider, CssBaseline } from '@mui/material';
import authrTheme from './theme';
import Layout from './components/Layout';
import LoadingScreen from './components/LoadingScreen';
import RequireRole from './components/auth/RequireRole';
import { ROUTES, getRoleDefaultPath } from './routes';
import { ROUTE_REGISTRY } from './routeRegistry';
import { setAuthToken, setTokenRefreshedCallback } from './services/api';
import ErrorBoundary from './components/ErrorBoundary';

// Broker onboarding wizard — full-screen, no sidebar
const BrokerOnboardingWizard  = lazy(() => import('./pages/BrokerOnboardingWizard'));
// Firm setup wizard — full-screen, no sidebar (PB / firm_admin first login)
const FirmSetupWizardPage     = lazy(() => import('./pages/FirmSetupWizardPage'));
// Legal acceptance gate — full-screen, no sidebar (first login, or next login after a version bump)
const AcceptTermsPage         = lazy(() => import('./pages/AcceptTermsPage'));
// Carrier-audit page has its own built-in unauthenticated "Mode B" (token-based,
// no login) view — LayoutGuard needs a direct reference to render it standalone,
// bypassing the authenticated Layout chrome, for that case. See LayoutGuard below.
const CarrierAuditPage        = lazy(() => import('./pages/CarrierAuditPage'));

// Public-only pages (lazy)
const LandingPage             = lazy(() => import('./pages/LandingPage'));
const PrivacyPolicyPage       = lazy(() => import('./pages/PrivacyPolicyPage'));
const TermsOfServicePage      = lazy(() => import('./pages/TermsOfServicePage'));
const ComplaintsPolicyPage    = lazy(() => import('./pages/ComplaintsPolicyPage'));
const AboutPage               = lazy(() => import('./pages/AboutPage'));
const SecurityPage            = lazy(() => import('./pages/SecurityPage'));
const ContactPage             = lazy(() => import('./pages/ContactPage'));
const LoginPage               = lazy(() => import('./pages/LoginPage'));
const ForgotPasswordPage      = lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage       = lazy(() => import('./pages/ResetPasswordPage'));
const OnboardingPage          = lazy(() => import('./pages/OnboardingPage'));
const MGAFactFinderPage       = lazy(() => import('./pages/MGAFactFinderPage'));
const SelfAssessmentStartPage = lazy(() => import('./pages/SelfAssessmentStartPage'));
const SelfAssessmentPage      = lazy(() => import('./pages/SelfAssessmentPage'));
const BrokerRegistrationPage  = lazy(() => import('./pages/BrokerRegistrationPage'));
const TenantInviteAcceptPage  = lazy(() => import('./pages/TenantInviteAcceptPage'));
const TenantSignupPage        = lazy(() => import('./pages/TenantSignupPage'));
const LawFirmSignupPage       = lazy(() => import('./pages/LawFirmSignupPage'));
const SignupPage               = lazy(() => import('./pages/SignupPage'));
const ModeSelectPage          = lazy(() => import('./pages/ModeSelectPage'));
const FirmInviteAcceptPage    = lazy(() => import('./pages/FirmInviteAcceptPage'));
const FirmSelfRegisterPage            = lazy(() => import('./pages/FirmSelfRegisterPage'));
const JurisdictionVerifyPage          = lazy(() => import('./pages/JurisdictionVerifyPage'));
const NotFoundPage                    = lazy(() => import('./pages/NotFoundPage'));

// Super-admin pages (lazy)
const SuperAdminPage   = lazy(() => import('./pages/SuperAdminPage'));
const AdminFirmsPage   = lazy(() => import('./pages/AdminFirmsPage'));
const AdminFilingsPage = lazy(() => import('./pages/AdminFilingsPage'));
const AdminInboundUnmatchedPage = lazy(() => import('./pages/AdminInboundUnmatchedPage'));

// ── Auth guards ──────────────────────────────────────────────────────────────

/**
 * Wraps all registry routes. Handles token check, super-admin mode enforcement,
 * and renders <Layout /> (which uses <Outlet /> for child routes).
 */
function LayoutGuard() {
  const { auth, mode, activeTenantId, activeFirmId } = useContext(AuthContext);
  const location = useLocation();

  // BUG FIX: carrier-audit share links are token-based and meant to work
  // without a login session — CarrierAuditPage.js already has this built in
  // ("Mode B: carrier view, no login, scoped to one audit" per its own
  // comments), but this guard unconditionally redirected anyone without
  // auth.token to /login before that component ever got a chance to check
  // for the token, so every shared audit link sent to a carrier who wasn't
  // already logged in was dead on arrival. Scoped narrowly to this one path
  // + token combination so no other route's auth requirement is touched.
  if (!auth.token && location.pathname === '/carrier-audits') {
    const token = new URLSearchParams(location.search).get('token');
    if (token) return <CarrierAuditPage />;
  }

  if (!auth.token) return <Navigate to={ROUTES.LOGIN} replace />;
  // Legal acceptance takes priority over everything else below, including
  // the onboarding wizards -- a brand new user must accept before being
  // sent into broker onboarding or firm setup, not after.
  if (auth.user?.needsLegalAcceptance) return <Navigate to={ROUTES.ACCEPT_TERMS} replace />;
  if (auth.user?.isSuperAdmin && !mode) return <Navigate to={ROUTES.MODE_SELECT} replace />;
  if (mode === 'admin') return <Navigate to={ROUTES.SUPER_ADMIN} replace />;
  // Super admin in pb mode must have a firm or tenant selected, or their own JWT tenantId.
  if (auth.user?.isSuperAdmin && mode === 'pb' && !activeFirmId && !activeTenantId && !auth.user?.tenantId) {
    return <Navigate to="/super-admin/firms" replace />;
  }
  // Broker first login: redirect to onboarding wizard if not yet complete
  if (auth.user?.role === 'broker' && auth.user?.onboarding_completed !== true && !auth.user?.license_number) {
    return <Navigate to={ROUTES.BROKER_ONBOARDING} replace />;
  }
  // New firm_admin / deputy_pb first login: redirect to setup wizard when explicitly false.
  // principal_broker NEVER goes to the setup wizard — they already manage firms.
  // NULL means legacy user imported without wizard requirement.
  const setupWizardRoles = ['deputy_pb', 'firm_admin'];
  if (setupWizardRoles.includes(auth.user?.role) && auth.user?.onboarding_completed === false) {
    return <Navigate to={ROUTES.FIRM_SETUP} replace />;
  }
  return <Layout />;
}

function SuperAdminLayout() {
  const { auth, mode } = useContext(AuthContext);
  if (!auth.token) return <Navigate to={ROUTES.LOGIN} replace />;
  if (!auth.user?.isSuperAdmin) return <Navigate to={ROUTES.DASHBOARD} replace />;
  if (!mode) return <Navigate to={ROUTES.MODE_SELECT} replace />;
  if (mode === 'pb') return <Navigate to={ROUTES.DASHBOARD} replace />;
  return <Layout />;
}

function ModeSelectGuard() {
  const { auth, mode } = useContext(AuthContext);
  if (!auth.token) return <Navigate to={ROUTES.LOGIN} replace />;
  if (!auth.user?.isSuperAdmin) return <Navigate to={ROUTES.DASHBOARD} replace />;
  if (mode === 'admin') return <Navigate to={ROUTES.SUPER_ADMIN} replace />;
  if (mode === 'pb') return <Navigate to={ROUTES.DASHBOARD} replace />;
  return <ModeSelectPage />;
}

function BrokerOnboardingGuard() {
  const { auth } = useContext(AuthContext);
  if (!auth.token) return <Navigate to={ROUTES.LOGIN} replace />;
  return <BrokerOnboardingWizard />;
}

function AcceptTermsGuard() {
  const { auth } = useContext(AuthContext);
  if (!auth.token) return <Navigate to={ROUTES.LOGIN} replace />;
  // Someone who has already accepted (or never needed to) can't linger here
  if (!auth.user?.needsLegalAcceptance) return <Navigate to={ROUTES.DASHBOARD} replace />;
  return <AcceptTermsPage />;
}

function FirmSetupGuard() {
  const { auth } = useContext(AuthContext);
  if (!auth.token) return <Navigate to={ROUTES.LOGIN} replace />;
  // Safety net: principal_broker must never land in the firm setup wizard
  if (auth.user?.role === 'principal_broker') return <Navigate to={ROUTES.MY_FIRMS} replace />;
  return <FirmSetupWizardPage />;
}

// ── App ──────────────────────────────────────────────────────────────────────

function App() {
  const { auth, updateToken, mode } = useContext(AuthContext);

  useEffect(() => {
    if (auth.token) setAuthToken(auth.token);
  }, [auth.token]);

  useEffect(() => {
    setTokenRefreshedCallback((token, user) => updateToken(token, user));
  }, [updateToken]);

  // / → landing for guests, role-aware redirect for logged-in users
  const landingOrDashboard = () => {
    if (!auth.token) return <LandingPage />;
    if (auth.user?.isSuperAdmin && !mode) return <Navigate to={ROUTES.MODE_SELECT} replace />;
    if (mode === 'admin') return <Navigate to={ROUTES.SUPER_ADMIN} replace />;
    return <Navigate to={getRoleDefaultPath(auth.user)} replace />;
  };

  return (
    <Router>
      <Suspense fallback={<LoadingScreen />}>
        <Routes>

          {/* ── Public routes ──────────────────────────────────────────── */}
          <Route path="/"                        element={landingOrDashboard()} />
          <Route path="/privacy"                 element={<PrivacyPolicyPage />} />
          <Route path="/terms"                   element={<TermsOfServicePage />} />
          <Route path="/complaints-policy"       element={<ComplaintsPolicyPage />} />
          <Route path="/about"                   element={<AboutPage />} />
          <Route path="/security"                element={<SecurityPage />} />
          <Route path="/contact"                 element={<ContactPage />} />
          <Route path={ROUTES.LOGIN}             element={<LoginPage />} />
          <Route path="/forgot-password"         element={<ForgotPasswordPage />} />
          <Route path="/reset-password"          element={<ResetPasswordPage />} />
          <Route path="/onboarding/:token"       element={<OnboardingPage />} />
          <Route path="/fact-finder/:token"      element={<MGAFactFinderPage />} />
          <Route path="/self-assessment"         element={<SelfAssessmentStartPage />} />
          <Route path="/self-assessment/:token"  element={<SelfAssessmentPage />} />
          <Route path="/invite/:token"           element={<BrokerRegistrationPage />} />
          <Route path="/firm-invite/:token"      element={<FirmInviteAcceptPage />} />
          <Route path="/register"                element={<FirmSelfRegisterPage />} />
          <Route path="/firm-register"           element={<FirmSelfRegisterPage />} />
          <Route path="/join/:token"             element={<TenantInviteAcceptPage />} />
          <Route path="/signup"                  element={<TenantSignupPage />} />
          <Route path="/law-firm-signup"                element={<LawFirmSignupPage />} />
          <Route path="/verify/compliance/:uuid"        element={<JurisdictionVerifyPage />} />
          <Route path={ROUTES.TRIAL_SIGNUP}      element={<SignupPage />} />
          <Route path={ROUTES.MODE_SELECT}       element={<ModeSelectGuard />} />
          <Route path={ROUTES.BROKER_ONBOARDING} element={<BrokerOnboardingGuard />} />
          <Route path={ROUTES.ACCEPT_TERMS}      element={<AcceptTermsGuard />} />
          <Route path={ROUTES.FIRM_SETUP}        element={<FirmSetupGuard />} />

          {/* ── Protected: ROUTE_REGISTRY (all roles + role-restricted) ── */}
          <Route element={<LayoutGuard />}>
            {ROUTE_REGISTRY.map(route => (
              <Route
                key={route.path}
                path={route.path}
                element={
                  <RequireRole allowedRoles={route.allowedRoles} superAdminOnly={route.superAdminOnly}>
                    {route.element}
                  </RequireRole>
                }
              />
            ))}
          </Route>

          {/* ── Protected: super admin only ─────────────────────────────── */}
          <Route element={<SuperAdminLayout />}>
            <Route path={ROUTES.SUPER_ADMIN}   element={<SuperAdminPage />} />
            <Route path="/super-admin/firms"   element={<AdminFirmsPage />} />
            <Route path="/super-admin/filings" element={<AdminFilingsPage />} />
            <Route path="/super-admin/inbound-unmatched" element={<AdminInboundUnmatchedPage />} />
          </Route>

          {/* ── /overview alias → /dashboard ───────────────────────────── */}
          <Route path={ROUTES.OVERVIEW} element={<Navigate to={ROUTES.DASHBOARD} replace />} />

          {/* ── Catch-all ──────────────────────────────────────────────── */}
          <Route path="/*" element={
            !auth.token                          ? <Navigate to="/" replace /> :
            (auth.user?.isSuperAdmin && !mode)   ? <Navigate to={ROUTES.MODE_SELECT} replace /> :
            mode === 'admin'                     ? <Navigate to={ROUTES.SUPER_ADMIN} replace /> :
                                                   <NotFoundPage />
          } />

        </Routes>
      </Suspense>
    </Router>
  );
}

export default function AppWithProvider() {
  return (
    <ErrorBoundary>
      <ThemeProvider theme={authrTheme}>
        <CssBaseline />
        <AuthProvider>
          <App />
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}
